import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import type { MetricValue } from '../../../types/insightsTypes';
import DataSourceBadge from './DataSourceBadge';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface FactorBreakdown {
  dti: MetricValue;
  liquid_reserves: MetricValue;
  cash_flow_stability: MetricValue;
  savings: MetricValue;
  debt: MetricValue;
  payment_history: MetricValue;
}

interface FinancialHealthRadarChartProps {
  /** Factor breakdown from useInsightsData — only live axes are rendered */
  factors?: FactorBreakdown;
}

const FinancialHealthRadarChart: React.FC<FinancialHealthRadarChartProps> = ({ factors }) => {
  // Only render axes with status === 'live'
  const liveAxes = factors
    ? [
        { label: 'Debt to Income', metric: factors.dti },
        { label: 'Liquid Reserves', metric: factors.liquid_reserves },
        { label: 'Cash Flow', metric: factors.cash_flow_stability },
      ].filter((a) => a.metric.status === 'live' && a.metric.value != null)
    : [];

  const notConnectedAxes = factors
    ? [
        { label: 'Savings', metric: factors.savings },
        { label: 'Debt', metric: factors.debt },
        { label: 'Payment History', metric: factors.payment_history },
      ].filter((a) => a.metric.status === 'not_connected')
    : [];

  if (liveAxes.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-slate-400 text-sm">
        No factor data available yet.
      </div>
    );
  }

  const data = {
    labels: liveAxes.map((a) => a.label),
    datasets: [
      {
        label: 'Financial Health Factors',
        data: liveAxes.map((a) => a.metric.value ?? 0),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: '#8b5cf6',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `${context.label}: ${context.parsed.r?.toFixed(1) ?? '—'}`;
          }
        }
      }
    },
    scales: {
      r: {
        angleLines: { color: 'rgba(148, 163, 184, 0.2)' },
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        pointLabels: { color: '#64748b', font: { size: 11 } },
        ticks: { color: '#94a3b8', font: { size: 10 }, backdropColor: 'transparent' },
        min: 0,
        max: 100
      }
    }
  };

  return (
    <div>
      <div className="h-72">
        <Radar data={data} options={options} />
      </div>
      {notConnectedAxes.length > 0 && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
          <DataSourceBadge status="not_connected" compact />
          <span>
            {notConnectedAxes.map((a) => a.label).join(', ')} — Requires Algorithm Extension
          </span>
        </div>
      )}
    </div>
  );
};

export default FinancialHealthRadarChart;